import { useAction, useAtom } from "@reatom/npm-react";
import {
  TemplatePreviewProductDto,
  TitleTemplateDto,
} from "definitions/exporter";
import { useEffect, useMemo, useRef } from "react";
import { getTemplatePreviewRequest } from "src/pages/ExportPage/store/fetchers";

import { isTemplateApiError, type TemplateApiError } from "./templateApiErrors";
import { PREVIEW_DEBOUNCE_MS } from "./TemplatesForm.constants";

interface UseTemplatePreviewArgs {
  titleTemplate?: TitleTemplateDto;
  descriptionTemplate?: TitleTemplateDto;
  products: TemplatePreviewProductDto[];
  enabled: boolean;
}

// Глубокий equals по интересующим полям через JSON.stringify — payload маленький
// (≤10 товаров с params/properties), сериализация дешёвая, гонять JSON.parse
// для true deep equals оверкилл.
const stableKey = (args: UseTemplatePreviewArgs) =>
  JSON.stringify({
    titleTemplate: args.titleTemplate,
    descriptionTemplate: args.descriptionTemplate,
    products: args.products,
    enabled: args.enabled,
  });

// Дебаунсированная обёртка над reatomAsync getTemplatePreviewRequest. Сам
// fetcher не дебаунсит — он только умеет abort'ить предыдущий запрос. Дебаунс
// нужен здесь, чтобы не сериализовать payload и не дёргать сеть на каждый
// keystroke юзера; abort страхует на случай быстрых смен.
export const useTemplatePreview = (args: UseTemplatePreviewArgs) => {
  const [data] = useAtom(getTemplatePreviewRequest.dataAtom);
  const [isLoading] = useAtom(getTemplatePreviewRequest.isLoadingAtom);
  const [rawError] = useAtom(getTemplatePreviewRequest.errorAtom);
  const fetchPreview = useAction(getTemplatePreviewRequest);

  const key = useMemo(
    () => stableKey(args),
    [args.titleTemplate, args.descriptionTemplate, args.products, args.enabled],
  );
  const lastKey = useRef<string | null>(null);

  useEffect(() => {
    if (
      !args.enabled ||
      (!args.titleTemplate && !args.descriptionTemplate) ||
      args.products.length === 0
    ) {
      return;
    }
    if (lastKey.current === key) return;
    lastKey.current = key;

    const timer = setTimeout(() => {
      fetchPreview({
        titleTemplate: args.titleTemplate,
        descriptionTemplate: args.descriptionTemplate,
        products: args.products,
      });
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const error: TemplateApiError | null = rawError
    ? isTemplateApiError(rawError)
      ? rawError
      : { kind: "other", message: String(rawError) }
    : null;

  return { data, isLoading, error };
};
